import { cx } from "shared/utils";
import { Hide, Show } from "shared/components";
import { ContentTitle } from "views/applications-id-report/components/ContentTitle";
import { IncomeType, ReverifyRequest } from "shared/types";
import { useCombinedIncomeContext } from "views/applications-id-report/slices/CombinedIncomeReportSlice/providers/useCombinedIncome";
import { TablesSlice } from "../..";
import { VerifiedReportedStats } from "./VerifiedReportedStats";
import { VerifiedHeader } from "../components/VerifiedHeader";
import { OfferLetterAssets } from "../components/OfferLetterAssets";
import { WarningRecommendation } from "../../components/WarningRecommendation";
import { AssetItem } from "views/applications-id-report/components/SmartRender/AssetItem";
import { VerifiedItem } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/verified";
import {
  CombinedIncome,
  UploadedIncomeSource,
  VerificationSpecificAdditionaData
} from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/income";
import { Dispatch, SetStateAction } from "react";
import { UnverifiedIncomeSourceTable } from "./UnverifiedIncomeSourceTable";
import { PayrollDocuments } from "./PayrollDocuments";
import { ReverifyInitiatedBy } from "../../../ReverifySlice/components/ReverifyInitiatedBy";
import { IncomeReverifyTabs } from "../../../ReverifySlice/components/IncomeReverifyTabs";
import { ReverifyProcessingStatus } from "../../../ReverifySlice/components/ReverifyProcessingStatus";
import { ReverifyAttempt } from "../../../ReverifySlice/types";
import { isReverifyTabAPlaceholder } from "../../../ReverifySlice/utils";
import { Banner } from "shared/components/Banner";
import { HugeiconsIcon } from "@hugeicons/react";
import { InformationCircleIcon } from "@hugeicons-pro/core-solid-rounded";

interface VerifiedContentProps {
  income: CombinedIncome;
  nonEmptyReverifyAttempt?: VerifiedItem | VerifiedItem[];
  additionalData?: VerificationSpecificAdditionaData | null;
  unverifiedIncomeSources: UploadedIncomeSource[];
}

const VerifiedContent = ({
  nonEmptyReverifyAttempt,
  additionalData,
  income,
  unverifiedIncomeSources
}: VerifiedContentProps) => {
  const { incomeSourceProvidersMap, incomeSourceTrustScoresMap } =
    useCombinedIncomeContext();
  if (nonEmptyReverifyAttempt)
    return (
      <TablesSlice
        nonEmptyReverifyAttempt={nonEmptyReverifyAttempt}
        incomeStatus={income.status}
        additionalData={additionalData}
        incomeSourceProviders={incomeSourceProvidersMap}
        incomeSourceTrustScores={incomeSourceTrustScoresMap}
      />
    );
  const showUnverifiedIncomeSources =
    ["pending", "finished_by_override"].includes(income.status) &&
    Boolean(unverifiedIncomeSources.length) &&
    // For when an income is reverified after being manually overrode
    income.income_sources_ids?.length === 0;

  if (showUnverifiedIncomeSources)
    return (
      <UnverifiedIncomeSourceTable incomeSources={unverifiedIncomeSources} />
    );
  const hasAssets = "assets" in income;

  return (
    <>
      <p className="text-base text-content-500">No verified income sources</p>
      <div className="h-[1px] w-full bg-alpha-40 my-3" />
      <div className="flex flex-wrap space-x-4">
        {hasAssets &&
          income.assets?.map(({ asset, global_id }) => (
            <AssetItem
              key={global_id}
              url={asset?.url || ""}
              content_type={asset?.content_type || ""}
              filename={asset?.filename || ""}
              assetName={asset?.filename || ""}
            />
          ))}
      </div>
    </>
  );
};

interface VerifiedIncomeDetailsProps {
  reverifyAttempts: ReverifyAttempt[];
  selectedReverifyAttempt?: ReverifyAttempt;
  income: CombinedIncome;
  selectedReverifyAttemptIdx: number;
  setSelectedReverifyAttemptIdx: Dispatch<SetStateAction<number>>;
  additionalData?: VerificationSpecificAdditionaData | null;
  pendingReverifyRequest?: ReverifyRequest | null;
}

export const VerifiedIncomeDetails = ({
  reverifyAttempts,
  selectedReverifyAttempt,
  income,
  selectedReverifyAttemptIdx,
  setSelectedReverifyAttemptIdx,
  additionalData,
  pendingReverifyRequest
}: VerifiedIncomeDetailsProps) => {
  const {
    applicationId,
    applicantId,
    currentlySelectedVerificationId,
    verificationStatus,
    refetch,
    setHideProjectedDebtSection
  } = useCombinedIncomeContext();
  const isReverifyPlaceholder = isReverifyTabAPlaceholder(
    selectedReverifyAttempt
  );
  const unverifiedIncomeSources = (income.sources ?? []).filter(
    (source) => source.status !== "reset"
  );
  // Argyle substitutes gross pay when a paystub reports no net, so the net totals can be part
  // gross while the documents table shows no net pay for that same stub.
  const hasEstimatedNetPay = ("items" in income ? income.items : []).some(
    (item) =>
      item.income_source_type === "payroll" && item.data?.net_pay_estimated
  );

  return (
    <>
      {reverifyAttempts.length > 1 ? (
        <>
          <IncomeReverifyTabs
            selectedReverifyAttemptIdx={selectedReverifyAttemptIdx}
            setSelectedReverifyAttemptIdx={setSelectedReverifyAttemptIdx}
            setHideProjectedDebtSection={setHideProjectedDebtSection}
            reverifyAttempts={reverifyAttempts}
            pendingReverifyRequest={pendingReverifyRequest}
          />
          <OfferLetterAssets income={income} />
        </>
      ) : (
        <ContentTitle
          title={
            income.type === IncomeType.OfferLetter
              ? "Offer letter"
              : "Verification method"
          }
          titleVariant="medium"
        >
          <Show if={income.type === IncomeType.OfferLetter}>
            <WarningRecommendation
              status="Rejected"
              type={income.type}
              rejectionReason={
                "assets" in income && !income.assets?.length
                  ? "No file was uploaded"
                  : ""
              }
              className="mb-4"
            />
          </Show>
          <OfferLetterAssets income={income} />
        </ContentTitle>
      )}

      <ReverifyInitiatedBy selectedReverifyAttempt={selectedReverifyAttempt} />
      <Hide if={income.type === IncomeType.OfferLetter}>
        <div
          key={selectedReverifyAttemptIdx}
          className={cx(
            "pb-4 animate-appear",
            reverifyAttempts.length > 1 ? "pt-4" : ""
          )}
        >
          {isReverifyPlaceholder ? (
            <ReverifyProcessingStatus
              income={income}
              applicationId={applicationId}
              verificationId={currentlySelectedVerificationId}
              applicantId={applicantId}
              selectedReverifyAttempt={selectedReverifyAttempt}
              verificationStatus={verificationStatus}
              onCancelled={refetch}
            />
          ) : (
            <>
              <VerifiedHeader
                selectedReverifyAttempt={selectedReverifyAttempt}
                income={income}
              />
              <VerifiedReportedStats
                selectedReverifyAttempt={selectedReverifyAttempt}
              />
              <VerifiedContent
                unverifiedIncomeSources={unverifiedIncomeSources}
                nonEmptyReverifyAttempt={selectedReverifyAttempt}
                additionalData={additionalData}
                income={income}
              />
              <Show if={hasEstimatedNetPay}>
                <Banner
                  variant="warning"
                  textTheme="base"
                  className="mt-4 rounded-lg"
                  body="Some paystubs did not report net pay, so their gross pay was used in the net totals. Those paystubs show no net pay in the documents below."
                  HugeIcon={
                    <HugeiconsIcon
                      icon={InformationCircleIcon}
                      className="shrink-0 text-warning-500"
                      size="20"
                    />
                  }
                />
              </Show>
              <PayrollDocuments income={income} />
            </>
          )}
        </div>
      </Hide>
    </>
  );
};
